import { ReactElement, useMemo } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { find } from 'lodash-es'
import { ToggleButton, ToggleButtonGroup, Typography, Stack } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { GenericErrorBoundaryFallback } from '@ui/errors'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore, useResultsStore } from '@editorStores'
import { getMemberCheckSettings, getModel } from '@queries'
import { updateElementCrossSectionAssignment, updateElementCrossSectionPolicy } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import useElementCrossSectionAssignment from '../../hooks/useElementCrossSectionAssignment'
import CrossSectionInfo from '../CrossSectionInfo'
import SingleCSForm from './components/SingleCSForm'
import useRecomputeMemberChecks from './hooks/useRecomputeMemberChecks'

interface Props {
  selectedElement: string
  elementType: ElementTypes
  invalidateResults?: boolean
  onSave?: () => void
  warning?: string
  alternateMutation?: {
    mutateAsync: (data: CrossSection) => Promise<unknown>
    isLoading: boolean
  }
  disableIfNotDirty?: boolean
}

const isPolicyElementType = (elementType: ElementTypes) =>
  elementType === 'rips' || elementType === 'lintels'

const SingleElementCSForm = ({
  selectedElement,
  elementType,
  invalidateResults = true,
  onSave = undefined,
  warning,
  alternateMutation,
  disableIfNotDirty = true,
}: Props): ReactElement => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const recomputeMemberChecks = useRecomputeMemberChecks()

  const elementCrossSectionAssignment = useElementCrossSectionAssignment()

  const rips = useModelStore(state => state.model.rips)
  const lintels = useModelStore(state => state.model.lintels)

  const standardRipChecks = useResultsStore(state => state.standardRipChecks)

  const positionPolicy = useMemo((): CrossSectionPolicy | undefined => {
    if (!isPolicyElementType(elementType)) return undefined
    const position =
      elementType === 'rips'
        ? find(rips, { position_guid: selectedElement })
        : find(lintels, { position_guid: selectedElement })
    return (
      position?.crossSectionPolicy ??
      find(elementCrossSectionAssignment, ['element_guid', selectedElement])?.crossSectionPolicy
    )
  }, [elementType, selectedElement, rips, lintels, elementCrossSectionAssignment])

  const selectedElementCrossSection = useMemo(() => {
    if (!selectedElement) return undefined
    if (
      elementType === 'beams' ||
      elementType === 'purlins' ||
      elementType === 'columns' ||
      elementType === 'rips' ||
      elementType === 'lintels'
    ) {
      return find(elementCrossSectionAssignment, ['element_guid', selectedElement])?.element_cs
    } else if (elementType === 'standard_rips') {
      const check = find(standardRipChecks, check => {
        return check.standard_rip_member.guid === selectedElement
      })
      return check?.standard_rip_member.cross_section
    }
  }, [selectedElement, elementType, elementCrossSectionAssignment, standardRipChecks])

  const invalidateAfterCsChange = async () => {
    await queryClient.invalidateQueries(getModel.getKey(projectId))
    await queryClient.invalidateQueries(getMemberCheckSettings.getKey(projectId))
    // Keeps the member's checks in line with its new cross section (as the results view does) and
    // invalidates the results once done.
    if (invalidateResults) recomputeMemberChecks(selectedElement)
    else
      enqueueSnackbar(
        'Sie müssen die Berechnung neu starten um die neuen Einstellungen zu verwenden',
        {
          variant: 'warning',
        },
      )
  }

  const { mutateAsync, isLoading } = useMutation(
    (data: CrossSection) => {
      const elementCS = { element_guid: selectedElement, element_cs: data }
      return updateElementCrossSectionAssignment.request(projectId as string, elementCS)
    },
    {
      onMutate: async () => {
        if (onSave) {
          onSave()
        }
      },
      onSuccess: async () => {
        await invalidateAfterCsChange()
        enqueueSnackbar('Querschnitt erfolgreich gespeichert', {
          variant: 'success',
        })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Querschnitts'), {
          variant: 'error',
        })
        queryClient.invalidateQueries(getModel.getKey(projectId))
      },
    },
  )

  const { mutateAsync: mutatePolicy, isLoading: isPolicyLoading } = useMutation(
    (policy: CrossSectionPolicy) =>
      updateElementCrossSectionPolicy.request(projectId as string, {
        element_guids: [selectedElement],
        crossSectionPolicy: policy,
        userDefinedCrossSection: policy === 'AssemblyStandard' ? null : undefined,
      }),
    {
      onSuccess: async () => {
        await invalidateAfterCsChange()
        enqueueSnackbar('Querschnitte erfolgreich gespeichert', {
          variant: 'success',
        })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Querschnitte'), {
          variant: 'error',
        })
      },
    },
  )

  if (elementType === 'standard_rips') {
    return <CrossSectionInfo crossSection={selectedElementCrossSection} />
  }

  const showPolicyToggle = isPolicyElementType(elementType)
  const effectivePolicy: CrossSectionPolicy = positionPolicy ?? 'UserDefined'
  const isAssemblyStandard = effectivePolicy === 'AssemblyStandard'

  return (
    <ErrorBoundary FallbackComponent={GenericErrorBoundaryFallback}>
      <Stack spacing={1}>
        {showPolicyToggle && (
          <>
            <Typography variant="body2">Querschnittsquelle</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              fullWidth
              value={effectivePolicy}
              disabled={isPolicyLoading}
              onChange={(_, value: CrossSectionPolicy | null) => {
                if (!value || value === effectivePolicy) return
                void mutatePolicy(value)
              }}
              data-cy="cross-section-policy-toggle"
            >
              <ToggleButton value="AssemblyStandard" data-cy="cs-policy-assembly-standard">
                Aus Aufbau
              </ToggleButton>
              <ToggleButton value="UserDefined" data-cy="cs-policy-user-defined">
                Benutzerdefiniert
              </ToggleButton>
            </ToggleButtonGroup>
          </>
        )}

        <SingleCSForm
          mutateAsync={alternateMutation?.mutateAsync || mutateAsync}
          isLoading={alternateMutation?.isLoading || isLoading}
          selectedElement={selectedElement}
          elementType={elementType}
          defaultCS={selectedElementCrossSection}
          warning={warning}
          disableIfDirty={disableIfNotDirty}
          isDisabled={isAssemblyStandard && showPolicyToggle}
        />
      </Stack>
    </ErrorBoundary>
  )
}

export default SingleElementCSForm
